import React from 'react';
import styled from 'styled-components';
import { Card, Tabs, Tag } from 'antd';
import colors from 'Styles/Colors';

const SummaryTabs = ({ data }) => {
  const stallsSummary = data?.stallsSummary || [];
  const rvsSummary = data?.rvsSummary || [];
  const addOnsSummary = data?.addOnsSummary || [];

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const ProductItem = ({ product, type = 'stall' }) => {
    const hasDiscount = product.discountApplied;

    // Calculate discount info
    let discountInfo = null;
    if (hasDiscount) {
      if (product.discountRate === 0) {
        discountInfo = 'FREE';
      } else {
        // discountRate is the dollar amount to subtract, not a percentage
        discountInfo = `-${formatCurrency(product.discountRate)}`;
      }
    }

    return (
      <ProductItemContainer>
        <div className="product-row">
          {/* Nombre e ID */}
          <div className="product-name-section">
            <div className="product-name">{product.name}</div>
            <div className="product-id">ID: {product.id}</div>
            {hasDiscount && (
              <Tag color={product.discountRate === 0 ? 'green' : 'orange'} size="small" className="discount-tag">
                {discountInfo}
              </Tag>
            )}
          </div>

          {/* Sold/Available */}
          <div className="quantity-section">
            <div className="sold-available">
              <span className="sold-count">{product.qty}</span>
              {type !== 'addon' && (
                <>
                  <span className="divider">/</span>
                  <span className="available-count">{product.stockQty}</span>
                </>
              )}
            </div>
            <div className="quantity-label">{type === 'addon' ? 'sold' : 'sold/available'}</div>
          </div>

          {/* Precio */}
          <div className="price-section">
            <div className="price-value">{formatCurrency(product.price)}</div>
            <div className="price-period">{product.nightly ? '/night' : 'flat'}</div>
          </div>

          {/* Revenue */}
          <div className="revenue-section">
            <div className="revenue-value">{formatCurrency(product.revenue)}</div>
            {hasDiscount && product.originalRevenue !== product.revenue && (
              <div className="original-revenue">
                <span className="original-value">{formatCurrency(product.originalRevenue)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Discount info below if needed */}
        {hasDiscount && (
          <div className="discount-details">
            <div className="discount-info">
              {product.discountRate === 0 ? (
                <span>Free product promotion</span>
              ) : (
                <span>
                  {formatCurrency(product.discountRate)} discount applied
                  {product.discountStartDate && product.discountEndDate && (
                    <span className="discount-period">
                      ({new Date(product.discountStartDate).toLocaleDateString()} - {new Date(product.discountEndDate).toLocaleDateString()})
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
      </ProductItemContainer>
    );
  };

  const EmptyTab = ({ type }) => (
    <div className="empty-tab">
      <div className="empty-text">No {type} data available</div>
    </div>
  );

  const ProductsHeader = ({ type }) => (
    <ProductsHeaderContainer>
      <div className="header-column">Product Name & ID</div>
      <div className="header-column">{type === 'addon' ? 'Sold' : 'Sold / Available'}</div>
      <div className="header-column">Price</div>
      <div className="header-column">Revenue</div>
    </ProductsHeaderContainer>
  );

  const tabItems = [
    {
      key: 'stalls',
      label: `Stalls (${stallsSummary.length})`,
      children: (
        <TabContent>
          {stallsSummary.length > 0 ? (
            <>
              <ProductsHeader type="stall" />
              {stallsSummary.map((stall, index) => (
                <ProductItem key={stall.id || index} product={stall} type="stall" />
              ))}
            </>
          ) : (
            <EmptyTab type="stalls" />
          )}
        </TabContent>
      )
    },
    {
      key: 'rvs',
      label: `RVs (${rvsSummary.length})`,
      children: (
        <TabContent>
          {rvsSummary.length > 0 ? (
            <>
              <ProductsHeader type="rv" />
              {rvsSummary.map((rv, index) => (
                <ProductItem key={rv.id || index} product={rv} type="rv" />
              ))}
            </>
          ) : (
            <EmptyTab type="rvs" />
          )}
        </TabContent>
      )
    },
    {
      key: 'addons',
      label: `Add-ons (${addOnsSummary.length})`,
      children: (
        <TabContent>
          {addOnsSummary.length > 0 ? (
            <>
              <ProductsHeader type="addon" />
              {addOnsSummary.map((addon, index) => (
                <ProductItem key={addon.id || index} product={{ ...addon, nightly: false, stockQty: 'N/A' }} type="addon" />
              ))}
            </>
          ) : (
            <EmptyTab type="addons" />
          )}
        </TabContent>
      )
    }
  ];

  return (
    <SummaryContainer>
      <Card className="dashboard-card full-width">
        <StyledTabs defaultActiveKey="stalls" items={tabItems} size="large" />
      </Card>
    </SummaryContainer>
  );
};

const SummaryContainer = styled.div`
  width: 100%;

  .dashboard-card {
    height: 100%;

    &.full-width {
      width: 100%;
    }

    .ant-card-body {
      padding: 0;
    }
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    padding: 0 24px;
    margin-bottom: 0;

    .ant-tabs-tab {
      padding: 16px 24px;
      font-size: 14px;
      font-weight: 500;

      &.ant-tabs-tab-active {
        .ant-tabs-tab-btn {
          color: ${colors.primary};
          font-weight: 600;
        }
      }
    }

    .ant-tabs-ink-bar {
      background: ${colors.primary};
    }
  }

  .ant-tabs-content-holder {
    .ant-tabs-content {
      .ant-tabs-tabpane {
        padding: 0;
      }
    }
  }
`;

const TabContent = styled.div`
  max-height: 500px;
  overflow-y: auto;

  .empty-tab {
    text-align: center;
    padding: 60px 20px;

    .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .empty-text {
      font-size: 16px;
      color: ${colors.text.secondary};
    }
  }
`;

const ProductsHeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.2fr 1fr 1fr;
  gap: 20px;
  padding: 12px 24px;
  background: ${colors.background.primary};
  border-bottom: 2px solid ${colors.border.primary};
  position: sticky;
  top: 0;
  z-index: 5;

  .header-column {
    font-size: 12px;
    font-weight: 700;
    color: ${colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:nth-child(2),
    &:nth-child(3) {
      text-align: center;
    }

    &:nth-child(4) {
      text-align: right;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 16px;
  }
`;

const ProductItemContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${colors.border.primary};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.background.primary}50;
  }

  &:last-child {
    border-bottom: none;
  }

  .product-row {
    display: grid;
    grid-template-columns: 2fr 1.2fr 1fr 1fr;
    gap: 20px;
    align-items: center;
  }

  .product-name-section {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .product-name {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
      line-height: 1.2;
    }

    .product-id {
      font-size: 11px;
      color: ${colors.text.secondary};
      background: ${colors.background.primary};
      padding: 2px 6px;
      border-radius: 3px;
      width: fit-content;
    }

    .discount-tag {
      margin-top: 4px;
      width: fit-content;
    }
  }

  .quantity-section {
    text-align: center;

    .sold-available {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      margin-bottom: 2px;

      .sold-count {
        font-size: 16px;
        font-weight: 700;
        color: ${colors.text.primary};
        display: flex;
        align-items: center;
        gap: 4px;

        .warning-icon {
          cursor: help;
          font-size: 12px;
        }
      }

      .divider {
        font-size: 16px;
        color: ${colors.text.secondary};
        font-weight: 500;
      }

      .available-count {
        font-size: 16px;
        font-weight: 500;
        color: ${colors.text.secondary};
      }
    }

    .quantity-label {
      font-size: 10px;
      color: ${colors.text.secondary};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .price-section {
    text-align: center;

    .price-value {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
      margin-bottom: 2px;
    }

    .price-period {
      font-size: 10px;
      color: ${colors.text.secondary};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .revenue-section {
    text-align: right;

    .revenue-value {
      font-size: 18px;
      font-weight: 700;
      color: ${colors.primary};
      margin-bottom: 2px;
    }

    .original-revenue {
      .original-value {
        font-size: 12px;
        color: ${colors.text.secondary};
        text-decoration: line-through;
      }
    }
  }

  .discount-details {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${colors.border.primary}50;
    grid-column: 1 / -1;

    .discount-info {
      font-size: 12px;
      color: ${colors.text.secondary};
      font-style: italic;

      .discount-period {
        margin-left: 4px;
        color: ${colors.text.lightGray2};
      }
    }
  }

  @media (max-width: 768px) {
    padding: 12px 16px;

    .product-row {
      grid-template-columns: 1fr;
      gap: 12px;
      text-align: left;
    }

    .quantity-section,
    .price-section,
    .revenue-section {
      text-align: left;
    }

    .quantity-section .sold-available {
      justify-content: flex-start;
    }
  }

  @media (max-width: 1200px) {
    .product-row {
      grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 16px;
    }

    .product-name-section .product-name {
      font-size: 15px;
    }
  }
`;

export default SummaryTabs;
